import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import EmptyStateComponent from 'shared/components/EmptyStateComponent';
import styles from './commodityActivityDataEntryList.module.scss';
import {
  AdePageCommoditySize as pageSize,
  commodityEmptyState,
  commodityTotalCalculation,
  commodityValidationNote,
  adePage,
} from 'constants/constants';
import { LocationData } from 'models/ActivityDataEntryScopes/locationData.model';
import { IncompleteIcon } from 'views/ActivityDataEntry/activityDataEntry.util';
import CommodityListService from 'services/CommodityListService/commodityList.service';
import COProductCard from 'shared/components/COProductCard';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import COAlertModal from 'shared/components/COAlertModal';
import { AllProductsProps } from 'enums/AllProductsEnums';
import ValidationModal from 'shared/components/ScopeValidationModal';
import FarmCommodity from 'views/ActivityDataEntry/FarmCommodity';
import { Location } from 'models/ActivityDataEntryLocation/location.model';
import {
  IN_REVIEW_ROUTE,
  inReviewStatus,
  stageMapping,
  statusMappingWithStage,
  livestockStageMapping,
} from 'views/FarmProductPage/constant';
import LiveStockCommodity from 'views/LiveStockCommodity';
import { Col, Row } from 'antd';
import { useRedirectBasedOnProductStatus } from 'shared/utils/redirectBasedonProductStatus';
import { CommodityTypes } from 'constants/FarmboxConstants/stage0.constants';
import { useUserRole } from 'shared/hooks/useUserRole';
import { canCreate } from 'shared/utils/roleUtils';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';

interface CommodityActivityDataEntryListProps {
  locationScopeDetails?: LocationData;
  setModalStatus: Dispatch<SetStateAction<boolean>>;
  modalStatus: boolean;
  activeYear?: string;
  locationId?: string;
  setIsFarmCommodityModal?: Dispatch<SetStateAction<boolean>>;
  isFarmCommodityModal?: boolean;
  location?: Location;
  setIsLiveStockCommodityModal?: Dispatch<SetStateAction<boolean>>;
  isLiveStockCommodityModal?: boolean;
}

const CommodityActivityDataEntryList: FC<
  CommodityActivityDataEntryListProps
> = props => {
  const {
    locationScopeDetails,
    setModalStatus,
    modalStatus,
    activeYear,
    locationId,
    setIsFarmCommodityModal,
    isFarmCommodityModal,
    location,
    setIsLiveStockCommodityModal,
    isLiveStockCommodityModal,
  } = props;

  const { fetchCommodityList, deleteCommodity, loading, commodityListData } =
    CommodityListService();
  const redirectBasedOnProductStatus = useRedirectBasedOnProductStatus();
  const userRole = useUserRole();
  const canCreateCommodity = canCreate(userRole);

  const { scopeCompletionStatus } = locationScopeDetails || {};
  const { scope1Status, scope2Status } = scopeCompletionStatus || {};
  const isComplete = scope1Status && scope2Status;
  const [showDeleteModalState, setDeleteModalState] = useState<boolean>(false);
  const [deleteCommodityID, setDeleteCommodityId] = useState<null | number>(
    null
  );
  const [deletedCommodityType, setDeletedCommodityType] = useState<string>('');

  useEffect(() => {
    fetchCommodityList({
      year: activeYear,
      locationId,
      size: pageSize,
    });
  }, [activeYear]);

  const getModalSubtitle = () =>
    isComplete
      ? commodityEmptyState.SUBTITLE_2
      : commodityEmptyState.SUBTITLE_1;

  const handleOpenCreateCommodityModal = () => {
    if (!canCreateCommodity) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    if (!isComplete) setModalStatus(!isComplete);
    else setIsFarmCommodityModal?.(true);
  };
  const handleOpenCreateLiveStockCommodityModal = () => {
    if (!canCreateCommodity) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    if (!isComplete) setModalStatus(!isComplete);
    else setIsLiveStockCommodityModal?.(true);
  };

  const handleOpenDeleteModal = () => {
    setDeleteModalState(true);
  };
  const handleCloseDeleteModal = () => {
    setDeleteModalState(false);
  };
  const closeScopeModal = () => setModalStatus(false);

  const handleProductNavigation = (
    productId?: number,
    stage?: number,
    status?: string,
    commodityType?: string
  ) => {
    const mappedStage =
      commodityType === CommodityTypes.LIVE_STOCK
        ? livestockStageMapping[stage ? Number(stage + 1) : 1]
        : stageMapping[stage ? Number(stage + 1) : 1];
    const mappedStatus =
      statusMappingWithStage[
        status === inReviewStatus ? IN_REVIEW_ROUTE : status ?? ''
      ];
    redirectBasedOnProductStatus({
      productId: productId ?? '',
      mappedStatus: mappedStatus ?? '',
      mappedStage: mappedStage ?? '',
      commodityType: commodityType ?? '',
      adePage,
    });
  };

  const handleModalConfirm = async () => {
    if (deleteCommodityID !== null)
      await deleteCommodity(
        deleteCommodityID,
        deletedCommodityType === CommodityTypes.LIVE_STOCK
      );
    await fetchCommodityList({ year: activeYear, locationId, size: pageSize });
    handleCloseDeleteModal();
    setDeleteCommodityId(null);
  };
  const onDeleteClick = useCallback((id?: number, type?: string) => {
    handleOpenDeleteModal();
    setDeleteCommodityId(id ?? null);
    setDeletedCommodityType(type ?? '');
  }, []);
  return (
    <div className={styles.productListContainer}>
      {loading ? (
        <div className={styles.loaderAnimation}>
          <LoaderAnimation />
        </div>
      ) : (commodityListData?.meta?.totalItems ?? 0) < 1 ? (
        <Row
          align={'middle'}
          justify={'space-between'}
          className={styles.buttonContainer}>
          <Col
            span={11}
            onClick={handleOpenCreateCommodityModal}
            style={{
              opacity: canCreateCommodity ? 1 : 0.6,
              cursor: canCreateCommodity ? 'pointer' : 'not-allowed',
            }}>
            <EmptyStateComponent
              title={commodityEmptyState.AGRICULTURE_TITLE}
              subTitle={getModalSubtitle()}
            />
          </Col>
          <Col
            span={11}
            offset={1}
            onClick={handleOpenCreateLiveStockCommodityModal}
            style={{
              opacity: canCreateCommodity ? 1 : 0.6,
              cursor: canCreateCommodity ? 'pointer' : 'not-allowed',
            }}>
            <EmptyStateComponent
              title={commodityEmptyState.LIVE_STOCK_TITLE}
              subTitle={getModalSubtitle()}
            />
          </Col>
        </Row>
      ) : (
        <div className={styles.commodityOverview}>
          <div className={styles.calculationHeader}>
            {/*Ui for Revenue total*/}
            <div className={styles.calculationContainer}>
              <div className={styles.quantityContainer}>
                <p className={styles.quantity}>
                  {commodityTotalCalculation.QUANTITY_TITLE}
                </p>
                <p className={styles.value}>
                  {commodityListData?.totalQuantity}
                </p>
              </div>
              <div className={styles.quantityContainer}>
                <p className={styles.quantity}>
                  {commodityTotalCalculation.REVENUE_TITLE}
                </p>
                <p className={styles.value}>
                  {commodityListData?.totalRevenuePercentage}
                  {commodityTotalCalculation.PERCENTAGE_UNIT}
                </p>
              </div>
            </div>

            {/* Note for farm revenue calculation */}
            {(commodityListData?.totalRevenuePercentage ?? 0) < 100 && (
              <div className={styles.scopeAlertIcon}>
                {
                  <IncompleteIcon
                    msg={commodityValidationNote.MESSAGE}
                    color={commodityValidationNote.BACKGROUND_COLOR_STYLE}
                    overlayInnerStyle={{
                      color: commodityValidationNote.COLOR_STYLE,
                    }}
                  />
                }
              </div>
            )}
          </div>
          <div className={styles.commodityCardsContainer}>
            {commodityListData?.commodities?.map(data => (
              <COProductCard
                key={data.commodityId}
                data={data}
                navigateProductOverview={handleProductNavigation}
                calenderRow
                showStatus
                multiRowCard
                commodityCard
                commodityDetailsCard
                showDownloadOptions={data.status}
                onDeleteClick={onDeleteClick.bind(
                  null,
                  data?.commodityId,
                  data?.commodityType
                )}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modal for scope1 & scope2 validation */}
      <ValidationModal modalStatus={modalStatus} closeModal={closeScopeModal} />

      <COAlertModal
        handleModalCancel={handleCloseDeleteModal}
        handleModalConfirm={handleModalConfirm}
        showModalState={showDeleteModalState}
        deleteModal
        heading={AllProductsProps.DELETE_COMMODITY}
        subHeading={AllProductsProps.DELETE_COMMODITY_SUBHEADING}
      />
      {isFarmCommodityModal && (
        <FarmCommodity
          visible={isFarmCommodityModal}
          setVisible={setIsFarmCommodityModal}
          fetchCommodityList={fetchCommodityList}
          location={location}
          size={pageSize}
        />
      )}
      {isLiveStockCommodityModal && (
        <LiveStockCommodity
          visible={isLiveStockCommodityModal}
          setVisible={setIsLiveStockCommodityModal}
          fetchCommodityList={fetchCommodityList}
          location={location}
        />
      )}
    </div>
  );
};

export default CommodityActivityDataEntryList;
